import React from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { api, fileUrl } from "../api";
import RsvpForm from "../components/RsvpForm";
import { Seats, Where } from "../components/GatheringRow";
import { fmtDayLong, fmtTimeRange, kindLabel, relative } from "../lib/dates";
import {
  Rule,
  Footer,
  Loading,
  Nav,
  Verse,
  useBodyClass,
  useDocumentTitle,
} from "../components/Chrome";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function GatheringDetail() {
  const { id } = useParams();
  useBodyClass("inner");
  const { data: g, error, isLoading, mutate } = useSWR(`/gatherings/${id}`, fetcher);

  useDocumentTitle(g?.title || "Gathering", g?.blurb || undefined);

  if (isLoading) {
    return (
      <>
        <Nav />
        <Loading label="Finding it" testid="gathering-detail-loading" />
      </>
    );
  }

  if (error || !g) {
    return (
      <>
        <Nav />
        <header className="page-head">
          <div className="prose">
            <p className="eyebrow sc">
              <span className="num">&middot;</span>
              <span>Not found</span>
            </p>
            <h1>We don&rsquo;t have that one.</h1>
            <p className="standfirst">
              It may have passed, or the link may be mistyped.
            </p>
          </div>
        </header>
        <section data-testid="gathering-detail-notfound">
          <div className="prose">
            <Link to="/gatherings" className="act">
              See what&rsquo;s on &rarr;
            </Link>
          </div>
        </section>
        <Footer />
      </>
    );
  }

  const past = g.is_past;

  return (
    <>
      <Nav />
      <header className="page-head" data-testid="gathering-detail-head">
        <div className="prose">
          <p className="eyebrow sc">
            <span className="num">&middot;</span>
            <span>
              {kindLabel(g.kind)}
              {past
                ? " \u00b7 already happened"
                : g.when_text
                  ? ""
                  : ` \u00b7 ${relative(g.starts_at)}`}
            </span>
          </p>
          <h1>{g.title}</h1>
          {g.blurb ? <p className="standfirst">{g.blurb}</p> : null}
        </div>
      </header>

      <section data-testid="gathering-detail-body">
        <div className="prose">
          <dl className="gathering-meta">
            <div>
              <dt>When</dt>
              <dd data-testid="gathering-detail-when">
                {g.when_text ? (
                  <>
                    {g.when_text}
                    <br />
                    <em>date to be confirmed</em>
                  </>
                ) : (
                  <>
                    {fmtDayLong(g.starts_at)}
                    <br />
                    {fmtTimeRange(g.starts_at, g.ends_at)}
                  </>
                )}
              </dd>
            </div>
            <div>
              <dt>Where</dt>
              <dd>
                {g.is_tbc ? (
                  <em>To be confirmed</em>
                ) : (
                  <>
                    {g.venue || "Calgary"}
                    {g.address ? (
                      <>
                        <br />
                        {g.address}
                      </>
                    ) : g.address_hidden ? (
                      <>
                        <br />
                        <em style={{ color: "var(--ink-mute)" }}>
                          Address sent when you write
                        </em>
                      </>
                    ) : null}
                  </>
                )}
              </dd>
            </div>
            {/* Only worth a line when there is actually a limit. */}
            {g.capacity ? (
              <div>
                <dt>Seats</dt>
                <dd>
                  <Seats g={g} testid="gathering-detail-seats" />
                </dd>
              </div>
            ) : null}
          </dl>

          <div className="actions" style={{ marginTop: 0 }}>
            <a
              className="linkish"
              href={fileUrl(`/gatherings/${g.id}/calendar.ics`)}
              data-testid="gathering-detail-calendar-link"
            >
              Add to calendar (.ics)
            </a>
            <Link className="linkish" to="/visit" data-testid="gathering-detail-visit-link">
              What you&rsquo;d be walking into
            </Link>
          </div>
        </div>
      </section>

      <Rule />

      <section id="come" data-testid="gathering-detail-rsvp">
        <div className="form-shell" style={{ padding: "0 1.5rem" }}>
          {past ? (
            <div className="empty">
              <h3 style={{ marginBottom: ".8rem" }}>This one has happened.</h3>
              <p>
                There will be another. Have a look at what&rsquo;s coming up, or
                tell us you&rsquo;d like to come to the next one.
              </p>
              <p style={{ marginTop: "1.4rem" }}>
                <Link to="/gatherings" className="linkish">
                  See what&rsquo;s on &rarr;
                </Link>
              </p>
            </div>
          ) : !g.rsvp_open ? (
            <div className="empty">
              <h3 style={{ marginBottom: ".8rem" }}>RSVPs are closed for this one.</h3>
              <p>Do write to us anyway &mdash; sometimes there&rsquo;s room.</p>
              <p style={{ marginTop: "1.4rem" }}>
                <Link to="/join" className="linkish">
                  Get in touch &rarr;
                </Link>
              </p>
            </div>
          ) : (
            <RsvpForm gathering={g} onDone={() => mutate()} />
          )}
        </div>
      </section>

      <Verse />
      <Footer />
    </>
  );
}
